import { type ReactNode } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  StackLayout,
  ParagraphText,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import {
  InfoSection,
  InfoValueText,
} from 'components/info-section/info-section.component';

// Rich-text chunk renderer for the "Contact Customer Care" link in the
// mailing-address support note. Rendered as an inline link that matches the
// surrounding body text (14px) rather than the larger button-styled
// TextAction. Defined at module scope so it is a stable reference (avoids
// react/no-unstable-nested-components).
const renderSupportLink = (chunks: ReactNode) => (
  <Box
    as={Link}
    to={APP_MANAGED_ROUTES.SUPPORT}
    css={{
      display: 'inline',
      typography: '$bodyTwo',
      // Override Genesis' legacy global `a` styles (shouldApplyLegacyGlobalStyles),
      // which bold all anchors; !important is required to win (see header-nav).
      fontWeight: '400 !important',
      color: '$interactiveActionPrimary',
      textDecoration: 'underline',
    }}
  >
    {chunks}
  </Box>
);

// Read-only Mailing address card — identical on the view and edit screens.
// Receives the already-composed address lines from `usePersonalInfo`.
export const MailingAddressSection = ({
  streetLine,
  cityStateLine,
}: {
  streetLine: string;
  cityStateLine: string;
}) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));

  const addressValue =
    streetLine || cityStateLine ? (
      <StackLayout
        spacing="$none"
        horizontalAlignment={isBelowTablet ? 'start' : 'end'}
      >
        {streetLine && <InfoValueText>{streetLine}</InfoValueText>}
        {cityStateLine && <InfoValueText>{cityStateLine}</InfoValueText>}
      </StackLayout>
    ) : undefined;

  return (
    <InfoSection
      heading={formatMessage({ id: 'STR_MAILING_ADDRESS' })}
      compactLabels
      rows={[
        {
          id: 'address',
          label: formatMessage({ id: 'STR_ADDRESS' }),
          value: addressValue,
        },
      ]}
      footer={
        <ParagraphText css={{ typography: '$bodyTwo' }}>
          {formatMessage(
            { id: 'STR_MAILING_ADDRESS_SUPPORT_NOTE' },
            { link: renderSupportLink },
          )}
        </ParagraphText>
      }
    />
  );
};
